import { create } from "zustand";
import { devtools } from "zustand/middleware";

/**
 * 초기 openNav 값을 미디어 쿼리로 결정
 * - md 브레이크포인트(768px) 이상: true (사이드바 열림)
 * - md 미만: false (사이드바 닫힘)
 */
const getInitialOpenNav = (): boolean => {
  if (typeof window === "undefined") return true; // SSR 대비
  return window.matchMedia("(min-width: 768px)").matches;
};

export interface UIStoreState {
  // UI 상태
  alertModal: boolean;
  isFreeScroll: boolean;
  openUserPopover: boolean;
  shareListModalOpen: boolean;
  shareModalOpen: boolean;
  logoutConfirmOpen: boolean;

  // 네비게이션 상태
  openNav: boolean;
  pageName: string;
  referrerUrl: string;
  openGroup: boolean;
  openStorage: boolean;

  // UI 액션
  toggleAlertModal: (show?: boolean) => void;
  setIsFreeScroll: (isFreeScroll: boolean) => void;
  setOpenUserPopover: (open: boolean) => void;
  setShareListModalOpen: (open: boolean) => void;
  setShareModalOpen: (open: boolean) => void;
  setLogoutConfirmOpen: (open: boolean) => void;

  // 네비게이션 액션
  toggleNav: () => void;
  closeNav: () => void;
  setPageName: (pageName: string) => void;
  setReferrerUrl: (url: string) => void;
  setOpenGroup: (open: boolean) => void;
  setOpenStorage: (open: boolean) => void;
}

/**
 * UI 스토어
 * - UI 상태 관리 (모달, 스크롤 등)
 * - 네비게이션 상태 관리 (사이드바, 페이지명, 반응형)
 */
export const useUIStore = create<UIStoreState>()(
  devtools(
    (set) => ({
      // ============================================
      // 초기 상태
      // ============================================

      // UI 상태
      alertModal: false,
      isFreeScroll: false,
      openUserPopover: false,
      shareListModalOpen: false,
      shareModalOpen: false,
      logoutConfirmOpen: false,

      // 네비게이션 상태
      openNav: getInitialOpenNav(),
      pageName: "tutor",
      referrerUrl: "",
      openGroup: false,
      openStorage: false,

      // ============================================
      // UI 액션
      // ============================================

      // 알림 모달 토글
      toggleAlertModal: (show?: boolean) => {
        set((state) => ({
          alertModal: show !== undefined ? show : !state.alertModal,
        }));
      },

      // 자유 스크롤 모드 토글
      setIsFreeScroll: (isFreeScroll: boolean) => set({ isFreeScroll }),

      // 유저 팝오버 열기/닫기
      setOpenUserPopover: (open: boolean) => set({ openUserPopover: open }),

      // 공유 목록 모달 열기/닫기
      setShareListModalOpen: (open: boolean) =>
        set({ shareListModalOpen: open }),

      // 공유 모달 열기/닫기
      setShareModalOpen: (open: boolean) => set({ shareModalOpen: open }),

      // 로그아웃 확인 모달 열기/닫기
      setLogoutConfirmOpen: (open: boolean) => set({ logoutConfirmOpen: open }),

      // ============================================
      // 네비게이션 액션
      // ============================================

      // 네비게이션 토글
      toggleNav: () => {
        set((state) => ({
          openNav: !state.openNav,
        }));
      },

      // 네비게이션 닫기
      closeNav: () => set({ openNav: false }),

      // 페이지명 설정
      setPageName: (pageName: string) => set({ pageName }),

      // 리퍼러 URL 설정
      setReferrerUrl: (url: string) => set({ referrerUrl: url }),
      setOpenGroup: (open: boolean) => set({ openGroup: open }),
      setOpenStorage: (open: boolean) => set({ openStorage: open }),
    }),
    { name: "UIStore" } // Redux DevTools 이름
  )
);
